import type { ReactNode } from 'react'
import { useEffect } from 'react'
import { GTMProvider, useGTMDispatch } from '@elgorditosalsero/react-gtm-hook'
import { useMatches, useSearchParams } from 'react-router-dom'

import { config } from '@/config'
import { useImpersonation } from '@/contexts/impersonation/impersonation-context'
import { usePathname } from '@/hooks/use-pathname'
import { trackPage } from '@/lib/segment'

interface TrackerProps {
	children: ReactNode
}

function usePageName(): string | undefined {
	const matches = useMatches()
	const last = matches[matches.length - 1]
	return (last?.handle as { pageName?: string } | undefined)?.pageName
}

function GtmPageViewTracker({ children }: TrackerProps) {
	const dispatch = useGTMDispatch()
	const pathname = usePathname()
	const [searchParams] = useSearchParams()

	useEffect(() => {
		dispatch({ event: 'page_view', page: pathname })
	}, [dispatch, pathname, searchParams])

	return <>{children}</>
}

function SegmentPageViewTracker({ children }: TrackerProps) {
	const pathname = usePathname()
	const { isImpersonating } = useImpersonation()
	const pageName = usePageName()

	useEffect(() => {
		if (isImpersonating) return
		trackPage(pageName, { path: pathname })
	}, [pathname, isImpersonating, pageName])

	return <>{children}</>
}

interface AnalyticsProps {
	children: ReactNode
}

export function Analytics({ children }: AnalyticsProps) {
	let content = <>{children}</>

	if (!config.analyticsDisabled && config.segment?.writeKey) {
		content = <SegmentPageViewTracker>{content}</SegmentPageViewTracker>
	}

	if (!config.analyticsDisabled && config.gtm?.id) {
		content = (
			<GTMProvider state={{ id: config.gtm.id }}>
				<GtmPageViewTracker>{content}</GtmPageViewTracker>
			</GTMProvider>
		)
	}

	return content
}
